/* eslint-disable @typescript-eslint/no-explicit-any */
// Import Type
import { getBestUserType, UserType } from '../types/enums/User/UserType.data';
import { AIStatus } from '../types/enums/AI/AIStatus';
import { JobType } from '../types/enums/Job/JobType';
import { IUser } from '../types/interfaces/User/IUser';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { UserRole } from '../types/enums/User/UserRole';

// Check is contact is new 
export const isContactNew = (contact: any) => {
  return !contact?.last_activity || contact?.last_activity?.role === '';
}

// Is AI paused for this contact ?
export const isAIPausedForContact = (contact: any, AI: any) => {
  if (contact?.messages_settings?.ia_pause || !AI) {
    return true;
  }

  const isNew = isContactNew(contact);

  if (isNew) {
    return [AIStatus.pausedFull, AIStatus.pausedNewcontacts].includes(AI.status as AIStatus);
  }

  return AI.status === AIStatus.pausedFull;
}

// Get the interest level of a contact
export const getInterestLevel = (qualification: string) => {
  switch (qualification) {
    case 'not_available':
      return 'À venir';
    case 'not_interested':
      return 'Inactif';
    case 'medium':
      return 'En réflexion';
    case 'interested':
      return 'Intéressé';
    case 'considering':
      return 'En réflexion';
    case 'not_responsive':
      return 'Sans réponse';
    default:
      return 'À venir';
  }
};

export const extractWhatsAppPhoneNumber = (str: string) => {
  // This regex matches the string starting with "whatsapp:+" followed by one or more digits.
  const regex = /^whatsapp:\+(\d+)$/;
  const match = str.match(regex);
  return match ? match[1] : null;
}

// List of country codes based on country name + phone code
export const countryCodes = [
  { name: 'france', code: '33', digitsLength: 9, flag: '🇫🇷', shortName: 'FR', longName: 'France' },
  { name: 'belgium', code: '32', digitsLength: 9, flag: '🇧🇪', shortName: 'BE', longName: 'Belgique' },
  { name: 'switzerland', code: '41', digitsLength: 9, flag: '🇨🇭', shortName: 'CH', longName: 'Suisse' },
  { name: 'unitedStates', code: '1', digitsLength: 10, flag: '🇺🇸', shortName: 'US', longName: 'États-Unis' },
  { name: 'germany', code: '49', digitsLength: 10, flag: '🇩🇪', shortName: 'DE', longName: 'Allemagne' },
  { name: 'unitedKingdom', code: '44', digitsLength: 10, flag: '🇬🇧', shortName: 'GB', longName: 'Royaume-Uni' },
  { name: 'canada', code: '1', digitsLength: 10, flag: '🇨🇦', shortName: 'CA', longName: 'Canada' },
  { name: 'italy', code: '39', digitsLength: 10, flag: '🇮🇹', shortName: 'IT', longName: 'Italie' },
  { name: 'spain', code: '34', digitsLength: 9, flag: '🇪🇸', shortName: 'ES', longName: 'Espagne' },
  { name: 'china', code: '86', digitsLength: 11, flag: '🇨🇳', shortName: 'CN', longName: 'Chine' },
  { name: 'russia', code: '7', digitsLength: 10, flag: '🇷🇺', shortName: 'RU', longName: 'Russie' },
  { name: 'japan', code: '81', digitsLength: 10, flag: '🇯🇵', shortName: 'JP', longName: 'Japon' },
  { name: 'india', code: '91', digitsLength: 10, flag: '🇮🇳', shortName: 'IN', longName: 'Inde' },
  { name: 'brazil', code: '55', digitsLength: 11, flag: '🇧🇷', shortName: 'BR', longName: 'Brésil' },
  { name: 'southKorea', code: '82', digitsLength: 10, flag: '🇰🇷', shortName: 'KR', longName: 'Corée du Sud' },
  { name: 'australia', code: '61', digitsLength: 9, flag: '🇦🇺', shortName: 'AU', longName: 'Australie' },
  { name: 'saudiArabia', code: '966', digitsLength: 9, flag: '🇸🇦', shortName: 'SA', longName: 'Arabie Saoudite' },
  { name: 'turkey', code: '90', digitsLength: 10, flag: '🇹🇷', shortName: 'TR', longName: 'Turquie' },
  { name: 'mexico', code: '52', digitsLength: 10, flag: '🇲🇽', shortName: 'MX', longName: 'Mexique' },
  { name: 'indonesia', code: '62', digitsLength: 10, flag: '🇮🇩', shortName: 'ID', longName: 'Indonésie' },
  { name: 'iran', code: '98', digitsLength: 10, flag: '🇮🇷', shortName: 'IR', longName: 'Iran' },
  { name: 'southAfrica', code: '27', digitsLength: 9, flag: '🇿🇦', shortName: 'ZA', longName: 'Afrique du Sud' },
  { name: 'argentina', code: '54', digitsLength: 10, flag: '🇦🇷', shortName: 'AR', longName: 'Argentine' },
  { name: 'nigeria', code: '234', digitsLength: 10, flag: '🇳🇬', shortName: 'NG', longName: 'Nigéria' },
  { name: 'egypt', code: '20', digitsLength: 10, flag: '🇪🇬', shortName: 'EG', longName: 'Égypte' },
  { name: 'pakistan', code: '92', digitsLength: 10, flag: '🇵🇰', shortName: 'PK', longName: 'Pakistan' },
  { name: 'netherlands', code: '31', digitsLength: 9, flag: '🇳🇱', shortName: 'NL', longName: 'Pays-Bas' },
  { name: 'poland', code: '48', digitsLength: 9, flag: '🇵🇱', shortName: 'PL', longName: 'Pologne' },
  { name: 'ukraine', code: '380', digitsLength: 9, flag: '🇺🇦', shortName: 'UA', longName: 'Ukraine' },
  { name: 'israel', code: '972', digitsLength: 9, flag: '🇮🇱', shortName: 'IL', longName: 'Israël' },
  { name: 'afghanistan', code: '93', digitsLength: 9, flag: '🇦🇫', shortName: 'AF', longName: 'Afghanistan' },
  { name: 'albania', code: '355', digitsLength: 9, flag: '🇦🇱', shortName: 'AL', longName: 'Albanie' },
  { name: 'algeria', code: '213', digitsLength: 9, flag: '🇩🇿', shortName: 'DZ', longName: 'Algérie' },
  { name: 'andorra', code: '376', digitsLength: 6, flag: '🇦🇩', shortName: 'AD', longName: 'Andorre' },
  { name: 'angola', code: '244', digitsLength: 9, flag: '🇦🇴', shortName: 'AO', longName: 'Angola' },
  { name: 'armenia', code: '374', digitsLength: 8, flag: '🇦🇲', shortName: 'AM', longName: 'Arménie' },
  { name: 'azerbaijan', code: '994', digitsLength: 9, flag: '🇦🇿', shortName: 'AZ', longName: 'Azerbaïdjan' },
  { name: 'bangladesh', code: '880', digitsLength: 10, flag: '🇧🇩', shortName: 'BD', longName: 'Bangladesh' },
  { name: 'bosnia', code: '387', digitsLength: 8, flag: '🇧🇦', shortName: 'BA', longName: 'Bosnie-Herzégovine' },
  { name: 'bulgaria', code: '359', digitsLength: 9, flag: '🇧🇬', shortName: 'BG', longName: 'Bulgarie' },
  { name: 'chile', code: '56', digitsLength: 9, flag: '🇨🇱', shortName: 'CL', longName: 'Chili' },
  { name: 'colombia', code: '57', digitsLength: 10, flag: '🇨🇴', shortName: 'CO', longName: 'Colombie' },
  { name: 'czechRepublic', code: '420', digitsLength: 9, flag: '🇨🇿', shortName: 'CZ', longName: 'République Tchèque' },
  { name: 'denmark', code: '45', digitsLength: 8, flag: '🇩🇰', shortName: 'DK', longName: 'Danemark' },
  { name: 'estonia', code: '372', digitsLength: 8, flag: '🇪🇪', shortName: 'EE', longName: 'Estonie' },
  { name: 'ethiopia', code: '251', digitsLength: 9, flag: '🇪🇹', shortName: 'ET', longName: 'Éthiopie' },
  { name: 'finland', code: '358', digitsLength: 9, flag: '🇫🇮', shortName: 'FI', longName: 'Finlande' },
  { name: 'greece', code: '30', digitsLength: 10, flag: '🇬🇷', shortName: 'GR', longName: 'Grèce' },
  { name: 'guyane', code: '594', digitsLength: 9, flag: '🇬🇫', shortName: 'GF', longName: 'Guyane Française' },
  { name: 'hungary', code: '36', digitsLength: 9, flag: '🇭🇺', shortName: 'HU', longName: 'Hongrie' },
  { name: 'iraq', code: '964', digitsLength: 10, flag: '🇮🇶', shortName: 'IQ', longName: 'Irak' },
  { name: 'kazakhstan', code: '7', digitsLength: 10, flag: '🇰🇿', shortName: 'KZ', longName: 'Kazakhstan' },
  { name: 'kenya', code: '254', digitsLength: 9, flag: '🇰🇪', shortName: 'KE', longName: 'Kenya' },
  { name: 'latvia', code: '371', digitsLength: 8, flag: '🇱🇻', shortName: 'LV', longName: 'Lettonie' },
  { name: 'lebanon', code: '961', digitsLength: 8, flag: '🇱🇧', shortName: 'LB', longName: 'Liban' },
  { name: 'lithuania', code: '370', digitsLength: 8, flag: '🇱🇹', shortName: 'LT', longName: 'Lituanie' },
  { name: 'luxembourg', code: '352', digitsLength: 9, flag: '🇱🇺', shortName: 'LU', longName: 'Luxembourg' },
  { name: 'malaysia', code: '60', digitsLength: 9, flag: '🇲🇾', shortName: 'MY', longName: 'Malaisie' },
  { name: 'morocco', code: '212', digitsLength: 9, flag: '🇲🇦', shortName: 'MA', longName: 'Maroc' },
  { name: 'norway', code: '47', digitsLength: 8, flag: '🇳🇴', shortName: 'NO', longName: 'Norvège' },
  { name: 'peru', code: '51', digitsLength: 9, flag: '🇵🇪', shortName: 'PE', longName: 'Pérou' },
  { name: 'portugal', code: '351', digitsLength: 9, flag: '🇵🇹', shortName: 'PT', longName: 'Portugal' },
  { name: 'romania', code: '40', digitsLength: 10, flag: '🇷🇴', shortName: 'RO', longName: 'Roumanie' },
  { name: 'serbia', code: '381', digitsLength: 9, flag: '🇷🇸', shortName: 'RS', longName: 'Serbie' },
  { name: 'slovakia', code: '421', digitsLength: 9, flag: '🇸🇰', shortName: 'SK', longName: 'Slovaquie' },
  { name: 'slovenia', code: '386', digitsLength: 8, flag: '🇸🇮', shortName: 'SI', longName: 'Slovénie' },
  { name: 'sweden', code: '46', digitsLength: 9, flag: '🇸🇪', shortName: 'SE', longName: 'Suède' },
  { name: 'syria', code: '963', digitsLength: 9, flag: '🇸🇾', shortName: 'SY', longName: 'Syrie' },
  { name: 'thailand', code: '66', digitsLength: 9, flag: '🇹🇭', shortName: 'TH', longName: 'Thaïlande' },
  { name: 'tunisia', code: '216', digitsLength: 8, flag: '🇹🇳', shortName: 'TN', longName: 'Tunisie' },
  { name: 'unitedArabEmirates', code: '971', digitsLength: 9, flag: '🇦🇪', shortName: 'UAE', longName: 'Émirats Arabes Unis' },
  { name: 'venezuela', code: '58', digitsLength: 10, flag: '🇻🇪', shortName: 'VE', longName: 'Venezuela' },
  { name: 'vietnam', code: '84', digitsLength: 9, flag: '🇻🇳', shortName: 'VN', longName: 'Vietnam' },
];

export const getCountryByShortName = (shortName: string) => {
  return countryCodes.find((country) => country.shortName === shortName);
}

export const isPhoneNumberStartingWithTheGoodCountryCode = (phonenumber: string, oNumber: any) => {
  try {
    // Find country code based on the sender's country
    const countryCode = countryCodes.find((country) => country.name === oNumber.country);
    if (countryCode && phonenumber.startsWith(countryCode.code)) {
      return true;
    } else {
      return false;
    }
  } catch (e) {
    return false;
  }
}

// As the phone number is not always provided with the country code, we add it if it's missing (WhatsApp & others)
export const addCountryCodeToPhoneNumberIfNotPresent = (phonenumber: string, oNumber: any) => {
  try {
    // If phonenumber starts with one of the country codes in countryCodes.code & length is also matching, return the phonenumber as is
    if (oNumber.channel === 'whatsapp' && countryCodes.some((country) => phonenumber.startsWith(country.code) && phonenumber.length === (country.code.length + country.digitsLength))) {
      console.log('Country code already present, number should be good');
      return phonenumber;
    } else {
      // Let's add the country code (if the phone number doesn't start with a country code already) based on the sender's country
      const countryCode = countryCodes.find((country) => country.name === oNumber.country);
      if (countryCode) {
        const countryCodeLength = countryCode.code?.length || 0;
        // Get first X chars of phone number
        const phoneStart = phonenumber.substring(0, countryCodeLength);
        // Check if the phone number already starts with it?
        if (phoneStart === countryCode.code) {
          return phonenumber;
        } else {
          if (phonenumber.length === countryCode.digitsLength) {
            return countryCode.code + phonenumber;
          } else {
            // Fallback
            return phonenumber;
          }
        }
      } else {
        // Fallback
        return phonenumber;
      }
    }
  } catch (e) {
    // Fallback
    return phonenumber;
  }
};

// Transforms a Belgian phone number into a reachable phone number for SMS providers
export const transformToSenderOriginPhoneNumberCode = (phonenumber: string, sender: string) => {
  try {
    // 🇫🇷 France
    if (sender.startsWith('33')) {
      if (phonenumber.startsWith('06') || phonenumber.startsWith('07')) {
        return '33' + phonenumber.substring(1);
      } else if (phonenumber.startsWith('+33')) {
        return phonenumber.substring(1);
      } else if (phonenumber.startsWith('0033')) {
        return phonenumber.substring(2);
      }
      return phonenumber;
    }

    // 🇧🇪 Belgium
    if (sender.startsWith('32')) {
      if (phonenumber.startsWith('04')) {
        return '32' + phonenumber.substring(1);
      } else if (phonenumber.startsWith('+32')) {
        return phonenumber.substring(1);
      } else if (phonenumber.startsWith('0032')) {
        return phonenumber.substring(2);
      }
      return phonenumber;
    }

    // 🇨🇭 Suisse
    if (sender.startsWith('41')) {
      if (phonenumber.startsWith('07')) {
        return '41' + phonenumber.substring(1);
      }
      return phonenumber;
    }

    return phonenumber;
  } catch (error) {
    return phonenumber;
  }
};

export const getCountryFromPhoneNumber = (phoneNumber: string) => {
  return countryCodes.find((country) => phoneNumber.startsWith(country.code)) || countryCodes[0];
}

/**
 * Découpe un tableau en paquets de `size` éléments, le dernier pouvant être
 * partiel. Sert surtout à respecter la limite de 30 valeurs de l'opérateur
 * `in` de Firestore.
 *
 * Générique plutôt que `any[]` : le type d'entrée se propage à la sortie
 * (`T[] -> T[][]`), ce que la signature précédente perdait.
 *
 * `size <= 0` n'est pas gardé : aucun appelant n'en passe (tous passent une
 * constante — 10, 30, FIRESTORE_IN_OPERATOR_LIMIT). Ajouter une garde
 * changerait le contrat pour un cas qui n'existe pas ; c'est signalé ici
 * plutôt que codé.
 */
export const chunkArray = <T,>(array: T[], size: number): T[][] => {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
    array.slice(i * size, i * size + size)
  );
};

// Generated random string for ID collection Firebase
export const generateCustomDocId = (): string => {
  const length = 10;
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    result += charset.charAt(randomIndex);
  }
  return 'lp-' + result;
};

// Get the flag of the sender
export const getSenderCountryFlag = (sender: string) => {

  // France
  if (sender.startsWith('33')) {
    return '🇫🇷';
  }

  // Belgium
  if (sender.startsWith('32')) {
    return '🇧🇪';
  }

  // Suisse
  if (sender.startsWith('41')) {
    return '🇨🇭';
  }

  // Other
  return '🌍';
}

export const formatPhoneToCallableFormat = (phone: string): string => {
  phone = phone.replace(/\D/g, "");

  // Manage french numbers without area code
  if (/^0[67]\d{8}$/.test(phone)) {
    return `+33${phone.slice(1)}`;
  }

  // Manage french numbers with area code already present
  if (/^33[67]\d{8}$/.test(phone)) {
    return `+${phone}`;
  }

  // Manage international numbers (begin with "00" or "+")
  if (/^00\d{6,15}$/.test(phone)) {
    return `+${phone.slice(2)}`;
  }
  if (/^\d{6,15}$/.test(phone) && !phone.startsWith("0")) {
    return `+${phone}`;
  }

  return phone;
};

// Calculate Future Timestamp For Job
const JobDaysMapping: { [key in JobType]: number } = {
  [JobType.reply]: 0,
  [JobType.firstContact]: 0,
  [JobType.reminderNoresponse]: 0,
  [JobType.followUp30Days]: 30,
  [JobType.followUp90Days]: 90,
  [JobType.followUp180Days]: 180,
  [JobType.followUp365Days]: 365,
  [JobType.followUpToDo]: 0,
  [JobType.followUpDone]: 0,
  [JobType.smsAnalysis]: 0,
  [JobType.workflow]: 0,
};

/**
 *
 * @param {string} jobType
 * @return {number} -
 */

export function calculateFutureTimestampForJob(jobType: JobType) {
  const daysToAdd = JobDaysMapping[jobType];
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + daysToAdd);
  const futureTimestampInMilliseconds = currentDate.getTime();

  return futureTimestampInMilliseconds;
}

export const formatNumber = (number: number | string) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const isCurrentDateInRange = (dateRanges: any): boolean => {
  const currentDate = Date.now();

  const normalizedRanges = Array.isArray(dateRanges[0]) ? dateRanges : [dateRanges];

  return normalizedRanges.some(([start, end]: [number, number]) => {
    if (typeof start === "number" && typeof end === "number") {
      return currentDate >= start && currentDate <= end;
    }
    console.warn("Plage de dates invalide :", [start, end]);
    return false;
  });
};

// Validate if the input string is a valid email
export const isEmailValid = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Generate a unique ID
export const generateUniqueId = (prefix = 'uid-'): string => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `${prefix}${timestamp}-${randomPart}`;
};

// Array of first names and last names for random generation
const firstNames = [
  "Jean", "Marie", "Pierre", "Sophie", "Thomas", "Camille", "Nicolas", "Émilie",
  "Antoine", "Céline", "François", "Julie", "Michel", "Aurélie", "Philippe", "Nathalie",
  "David", "Caroline", "Stéphane", "Isabelle", "Lucas", "Emma", "Noah", "Léa", "Hugo", "Chloé", "Nathan", "Jade", "Louis", "Camille",
  "Gabriel", "Inès", "Raphaël", "Manon", "Ethan", "Sarah", "Arthur", "Lina", "Tom", "Zoé",
  "Enzo", "Anaïs", "Adam", "Clara", "Léo", "Mila", "Maxime", "Eva", "Julien", "Océane"
];

const lastNames = [
  "Martin", "Bernard", "Dubois", "Thomas", "Robert", "Richard", "Petit", "Durand",
  "Leroy", "Moreau", "Simon", "Laurent", "Lefebvre", "Michel", "Garcia", "David",
  "Bertrand", "Roux", "Vincent", "Fournier", "Perrin", "Renaud", "Marchand", "Henry", "Lucas", "Gauthier", "Meyer", "Jean", "Faure", "Carpentier",
  "Dupont", "Blanc", "Barbier", "Garnier", "Payet", "Chevalier", "Collin", "Da Silva", "Hubert", "Renard",
  "Legrand", "Lopez", "Masson", "Brun", "Baron", "Charles", "Bourgeois", "Gilbert", "Philippe", "Rodriguez"
];

// Array of random conversation contexts
const conversationContexts = [
  "Demande d'informations sur vos services",
  "Question sur vos tarifs",
  "Recherche de renseignements sur votre entreprise",
  "Intérêt pour une démo de votre produit",
  "Besoin d'aide pour résoudre un problème",
  "Demande de contact avec un conseiller",
  "Recherche d'informations complémentaires",
  "Marié(e) avec 2 enfants, recherche une solution familiale",
  "Célibataire, première acquisition en vue",
  "Jeune couple sans enfant, en recherche d'investissement",
  "Divorcé(e) avec garde alternée, besoin d'un logement adapté",
  "Cadre en entreprise, déménagement professionnel imminent",
  "Retraité(e) souhaitant se rapprocher de ses petits-enfants",
  "Jeune diplômé(e) pour un premier achat immobilier",
  "Parent solo avec 3 enfants, recherche plus grand",
  "Propriétaire souhaitant investir dans un bien locatif",
  "Couple de seniors cherchant à réduire la taille de leur logement",
  "Famille recomposée de 5 personnes en recherche active"
];

// Get a random element from an array
export const getRandomElement = (array: any[]) => {
  return array[Math.floor(Math.random() * array.length)];
};

// Generate a random first name and last name
export const generateRandomPerson = () => {
  return {
    firstName: getRandomElement(firstNames),
    lastName: getRandomElement(lastNames)
  };
};

// Generate a random conversation context
export const generateRandomContext = () => {
  return getRandomElement(conversationContexts);
};

// Generate random colors
export const generateColor = (count: number) => {
  return Array.from({ length: count }, () =>
    `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
  );
};

// ------------------------ Phone number utilities (Frontend & Backoffice ) ------------------------

// Clean phone number (remove all non-digits)
export const cleanPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

// Remove leading zeros
export const removeLeadingZeros = (phone: string): string => {
  if (!phone) return '';
  return phone.replace(/^0+/, '');
};

// Check if the phone number is valid
export const isValidPhoneNumber = (phone: string, expectedLength: number): boolean => {
  const cleanedPhone = cleanPhoneNumber(phone);
  return cleanedPhone.length === expectedLength;
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Format the full phone number with the country code
export const formatFullPhoneNumber = (phone: string, countryCode: string): string => {
  if (!phone) return '';

  const cleanedPhone = cleanPhoneNumber(phone);
  // If the phone number starts with the country code, return it as is
  if (cleanedPhone.startsWith(countryCode)) {
    return cleanedPhone;
  }

  // Remove leading zeros before adding the country code
  const phoneWithoutLeadingZeros = removeLeadingZeros(cleanedPhone);
  return countryCode + phoneWithoutLeadingZeros;
};

export const hidePhone = (phone: string) => {
  return phone.slice(0, -4) + '****';
}

export const hideEmail = (email: string) => {
  const [name, domain] = email.split('@');

  if (name.length <= 2) {
    return '*'.repeat(name.length) + '@' + domain;
  }

  const begin = name.slice(0, 2);
  const end = name.slice(-2);
  const middle = '*'.repeat(name.length - 4);

  return begin + middle + end + '@' + domain;
}

/**
 * Set local props type base on the best user's type for this subscription
 * @param oUserData - The user data
 * @param subscriptionId - The subscription ID
 * @returns The normalized user data
 */
export const normalizeUser = (oUserData: IUser, subscriptionId: string) => {
  return {
    ...oUserData,
    type: getBestUserType(oUserData.id_subscription[subscriptionId]?.roles as UserType[] || []),
  };
}

/**
 * Get a nested property from an object
 * @param obj - The object
 * @param path - The path to the property (ex: "props1.props2.props3")
 * @returns The nested property
 */
export const getNestedProp = (obj: Record<string, any>, path: string) => {
  return path.split('.').reduce((acc: Record<string, any>, key: string) => acc && acc[key], obj);
}

/**
 * Flatten an object (usefull for firebase updates without reupdating the whole object)
 * @param obj - The object (ex: { props1:value, props2:{ props21:value21, props22: value22 } })
 * @param deleteValue - The value to delete (ex: FieldValue.delete() from backend and deleteField from frontend)
 * @param recurrenceOptions { parentKey, result} - Options used for recurrence calls only, don't use them 
 * @returns The flattened object (ex: { "props1":value, "props2.props21":value21, "props2.props22":value22 })
 */
export const flattenObject = (obj: Record<string, any>, deleteValue = undefined, { parentKey = '', result = {} }: { parentKey?: string, result?: Record<string, any> } = {}) => {
  for (const [key, value] of Object.entries(obj)) {
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (
      value !== null &&
      value !== undefined &&
      typeof value === 'object' &&
      value !== deleteValue &&
      !Array.isArray(value) &&
      typeof value !== 'function'
    ) {
      flattenObject(value, deleteValue, { parentKey: newKey, result });
    } else {
      result[newKey] = value;
    }
  }

  return result;
}

export const unflattenObject = (obj: Record<string, any>) => {
  const result: Record<string, any> = {};

  for (const key in obj) {
    const parts = key.split('.');

    // On utilise reduce pour descendre dans l'arborescence
    parts.reduce((acc, part, index) => {
      // Si c'est le dernier segment, on affecte la valeur
      if (index === parts.length - 1) {
        acc[part] = obj[key];
      } else {
        // Sinon on crée l'objet parent s'il n'existe pas
        if (!acc[part]) acc[part] = {};
      }
      // On retourne l'objet de ce niveau pour le tour suivant
      return acc[part];
    }, result);
  }

  return result;
};

// Return the hour in specific timezone
export function getTzHours(date: Date, tz = 'Europe/Paris'): number {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: tz,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(date);

  const hourPart = parts.find((p) => p.type === 'hour');
  return hourPart ? parseInt(hourPart.value, 10) : 0;
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = phone.startsWith('+') ? phone : `+${phone}`;
  const parsed = parsePhoneNumberFromString(normalized);

  if (parsed) {
    return parsed.formatInternational();
  }

  // Fallback: return as-is if parsing fails
  return phone;
}

export const isLeedTeamUser = (user: IUser) => {
  return user.role === UserRole.admin || user.role === UserRole.owner || user.role === UserRole.maintainer;
}

/**
 * Format time in HH:MM:SS,mmm format
 * @param seconds - The number of seconds
 * @returns The time in HH:MM:SS,mmm format (string)
 */
export const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Format duration in milliseconds into human readable French string
 * Example: 5000000 -> "1 heure, 23 minutes, 20 secondes"
 * @param duration - The number of milliseconds
 * @returns The formatted duration
 */
export const formatDuration = (duration: number) => {
  const totalSeconds = Math.floor(duration / 1000);

  if (totalSeconds <= 0) {
    return '0 seconde';
  }

  const secondsInMinute = 60;
  const secondsInHour = 60 * secondsInMinute;
  const secondsInDay = 24 * secondsInHour;
  const secondsInMonth = 30 * secondsInDay;

  let remaining = totalSeconds;

  const months = Math.floor(remaining / secondsInMonth);
  remaining %= secondsInMonth;

  const days = Math.floor(remaining / secondsInDay);
  remaining %= secondsInDay;

  const hours = Math.floor(remaining / secondsInHour);
  remaining %= secondsInHour;

  const minutes = Math.floor(remaining / secondsInMinute);
  const seconds = remaining % secondsInMinute;

  const parts: string[] = [];

  const pushPart = (value: number, singular: string, plural: string) => {
    if (value > 0) {
      parts.push(`${value} ${value > 1 ? plural : singular}`);
    }
  };

  pushPart(months, 'mois', 'mois');
  pushPart(days, 'jour', 'jours');
  pushPart(hours, 'heure', 'heures');
  pushPart(minutes, 'minute', 'minutes');
  pushPart(seconds, 'seconde', 'secondes');

  return parts.join(', ');
}

// Check if mobile number is valid
// 🇫🇷 France, 🇧🇪 Belgium
export const isMobilePhoneValid = (phonenumber: string, sender: string) => {
  // 🇫🇷 France mobile numbers
  if (sender.startsWith('33')) {
    const frenchMobileRegex = /^(0|\+33|0033|33)(6|7)\d{8}$/;
    if (frenchMobileRegex.test(phonenumber)) {
      return true;
    }
  }

  // 🇧🇪 Belgium mobile numbers
  if (sender.startsWith('32')) {
    const belgiumMobileRegex = /^(0|\+32|0032|32)(4)\d{8}$/;
    if (belgiumMobileRegex.test(phonenumber)) {
      return true;
    }
  }

  return false;
}

export function normalizeString(input: string, options: { sizeCase?: 'lowercase' | 'uppercase' } = { sizeCase: 'lowercase' }): string {
  // Lowercase, remove accents, trim, and collapse whitespace.
  let normalized = input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (options.sizeCase === 'lowercase') {
    normalized = normalized.toLowerCase();
  } else if (options.sizeCase === 'uppercase') {
    normalized = normalized.toUpperCase();
  }
  normalized = normalized
    .trim()
    .replace(/\s+/g, ' ');
    
  return normalized;
}